import * as React from 'react'
import useSWR from 'swr'
import type { SWRConfiguration } from 'swr'

import { getWebsiteScreenshotBlob } from 'lib/api'

export const WEBSITE_SCREENSHOT_WIDTH = 1400

const defaultSWROptions: SWRConfiguration = {
	revalidateIfStale: false,
	revalidateOnFocus: false,
}

export function useCaptureScreenshot(previewUrl: string, options?: SWRConfiguration) {
	const {
		data: blob,
		isLoading,
		error,
	} = useSWR<Blob>(
		previewUrl ? ['website-screenshot', previewUrl] : null,
		async () =>
			getWebsiteScreenshotBlob({
				url: previewUrl,
				usage: 'widget-preview',
			}),
		{
			...defaultSWROptions,
			...options,
		},
	)

	// Kept in state (rather than a memoized object URL) because reactStrictMode double-invokes
	// effects in development: a useMemo'd URL would get revoked on the simulated unmount and
	// never recreated, breaking the image in dev only. This re-derives a fresh URL per mount.
	const [screenshotUrl, setScreenshotUrl] = React.useState<string>()

	React.useEffect(() => {
		if (!blob) {
			return
		}

		const objectUrl = URL.createObjectURL(blob)
		setScreenshotUrl(objectUrl)

		return () => {
			URL.revokeObjectURL(objectUrl)
			setScreenshotUrl(undefined)
		}
	}, [blob])

	return {
		screenshotUrl,
		isScreenshotUrlLoading: isLoading,
		error,
	}
}
